import { Entity } from '@api/api/entity-schema';
import { Drawer } from '@design/components/drawer/Drawer';
import { ColorScheme } from '@design/design-tokens/themes/theme-names';
import { DrawerContentWrapper } from '@terminal/components/drawer-content-wrapper/DrawerContentWrapper';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useThemeStore } from '@terminal/components/theme-controller/stores/useThemeStore';
import { useBrandsAndProducts } from '@terminal/react-query-hooks/knowledge-graph/useBrandsAndProducts';
import { useBrandAndProductDrawer } from '@terminal/widgets/knowledge-graph-widget/components/brands-and-products-tab/useBrandAndProductDrawer';

import styles from './BrandAndProductDrawer.module.scss';
import { BrandAndProductDrawerContent } from './components/BrandAndProductDrawerContent';

export interface BrandAndProductDrawerProps {
  entity: Entity;
}

export const BrandAndProductDrawer = ({
  entity,
}: BrandAndProductDrawerProps) => {
  const { closeDrawer, isDrawerOpen, product } = useBrandAndProductDrawer();
  const { data, error } = useBrandsAndProducts(
    entity.tag,
    isDrawerOpen && !!product
  );
  const isDark = useThemeStore(state => state.colorScheme === ColorScheme.Dark);

  if (error && isDrawerOpen) {
    throw error;
  }

  const productAndBrand = data?.find(item => item.product === product);
  const logoUrl = isDark
    ? entity.logo_urls.light_png
    : entity.logo_urls.dark_png;

  return (
    <Drawer
      data-testid="brand-product-exposure-drawer"
      isOpen={isDrawerOpen && !!productAndBrand}
      handleCloseClick={closeDrawer}
      headerButton={<div className={styles.headerTitle}>{product}</div>}
    >
      {productAndBrand && (
        <DrawerContentWrapper className={styles.drawerContent}>
          <ErrorBoundaryWrapper>
            <BrandAndProductDrawerContent
              entity={entity}
              logoUrl={logoUrl}
              productAndBrand={productAndBrand}
            />
          </ErrorBoundaryWrapper>
        </DrawerContentWrapper>
      )}
    </Drawer>
  );
};
